import React from 'react';
import { Check, FileUp, Plus, Square, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../components/ui/button';
import { Input } from '../../../../../../components/ui/input';
import { Label } from '../../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../components/ui/select';
import {
  COATING_CATEGORY_OPTIONS,
  type CoatingChecklistTemplateSection,
} from './coatingChecklistMockData';

const CRITERIA_OPTIONS = ['Accept/Reject/NA', 'Text', 'Value', 'Table', 'Dateandtime'] as const;
const CRITERIA_LABELS: Record<(typeof CRITERIA_OPTIONS)[number], string> = {
  'Accept/Reject/NA': 'Accept/Reject/NA',
  Text: 'Text',
  Value: 'Value',
  Table: 'Table',
  Dateandtime: 'Date time',
};
const UNIT_OPTIONS = ['µm', 'µS/cm', 'Rating', '%', '°C', 'min', 'N/A'] as const;
const COMPARATOR_OPTIONS = ['<', '<=', '>', '>=', '=', 'BETWEEN', 'None'] as const;

type ChecklistItem = CoatingChecklistTemplateSection['items'][number];
type CoatingItemField =
  | 'criteria'
  | 'unit'
  | 'comparator'
  | 'minValue'
  | 'maxValue'
  | 'value'
  | 'textValue'
  | 'dateTimeValue'
  | 'checkboxRequired'
  | 'fileUploadRequired'
  | 'tableData';

type CoatingTableData = NonNullable<ChecklistItem['tableData']>;

export interface CoatingChecklistTemplateTableProps {
  sections: CoatingChecklistTemplateSection[];
  onUpdateSectionCategory: (sectionId: string, category: string) => void;
  onRemoveSection: (sectionId: string) => void;
  onRemoveItem: (sectionId: string, itemId: string) => void;
  onUpdateItemDescription: (sectionId: string, itemId: string, description: string) => void;
  onUpdateItemField?: (sectionId: string, itemId: string, field: CoatingItemField, value: unknown) => void;
  onAddItem?: (sectionId: string) => void;
  sectionFooter?: (section: CoatingChecklistTemplateSection) => React.ReactNode;
  hideCategoryHeader?: boolean;
}

function defaultTableData(rowCount = 1, colCount = 1): CoatingTableData {
  return {
    rows: Array.from({ length: rowCount }, (_, i) => ({
      name: `Row ${i + 1}`,
      values: Array.from({ length: colCount }, () => ''),
      unit: '',
      comparator: '=',
      minValue: '',
      maxValue: '',
    })),
    columns: Array.from({ length: colCount }, (_, i) => `Column ${i + 1}`),
    mode: 'multiple',
  };
}

function normalizeTableData(item: ChecklistItem): CoatingTableData {
  if (item.tableData?.rows?.length && item.tableData?.columns?.length) return item.tableData;
  return defaultTableData();
}

function SectionBlock({
  section,
  canRemoveSection,
  onUpdateSectionCategory,
  onRemoveSection,
  onRemoveItem,
  onUpdateItemDescription,
  onUpdateItemField,
  onAddItem,
  sectionFooter,
  hideCategoryHeader,
}: {
  section: CoatingChecklistTemplateSection;
  canRemoveSection: boolean;
  onUpdateSectionCategory: (sid: string, category: string) => void;
  onRemoveSection: (sid: string) => void;
  onRemoveItem: (sid: string, itemId: string) => void;
  onUpdateItemDescription: (sid: string, itemId: string, description: string) => void;
  onUpdateItemField?: (sid: string, itemId: string, field: CoatingItemField, value: unknown) => void;
  onAddItem?: (sid: string) => void;
  sectionFooter?: (sec: CoatingChecklistTemplateSection) => React.ReactNode;
  hideCategoryHeader?: boolean;
}) {
  const showHeader = !hideCategoryHeader || canRemoveSection;
  const isReadOnly = !onUpdateItemField;

  const updateItemField = (item: ChecklistItem, field: CoatingItemField, value: unknown) => {
    onUpdateItemField?.(section.id, item.id, field, value);
  };

  const resizeTable = (item: ChecklistItem, rowCount: number, colCount: number) => {
    const safeRows = Math.max(1, Math.min(20, rowCount || 1));
    const safeCols = Math.max(1, Math.min(12, colCount || 1));
    const current = normalizeTableData(item);
    const rows = Array.from({ length: safeRows }, (_, rowIndex) => {
      const existing = current.rows[rowIndex];
      return {
        name: existing?.name || `Row ${rowIndex + 1}`,
        unit: existing?.unit || '',
        comparator: existing?.comparator || '=',
        minValue: existing?.minValue || '',
        maxValue: existing?.maxValue || '',
        values: Array.from({ length: safeCols }, (_, colIndex) => existing?.values?.[colIndex] || ''),
      };
    });
    const columns = Array.from({ length: safeCols }, (_, colIndex) => current.columns[colIndex] || `Column ${colIndex + 1}`);
    updateItemField(item, 'tableData', { ...current, rows, columns });
  };

  const updateTable = (item: ChecklistItem, updater: (table: CoatingTableData) => CoatingTableData) => {
    updateItemField(item, 'tableData', updater(normalizeTableData(item)));
  };

  const renderEntry = (item: ChecklistItem) => {
    const criteria = item.criteria || 'Accept/Reject/NA';
    const comparator = item.comparator || (criteria === 'Value' || criteria === 'Table' ? '=' : 'None');
    const showMin = comparator === '>' || comparator === '>=' || comparator === 'BETWEEN';
    const showMax = comparator === '<' || comparator === '<=' || comparator === '=' || comparator === 'BETWEEN';

    if (criteria === 'Accept/Reject/NA' || criteria === 'Text') {
      return null;
    }

    if (criteria === 'Dateandtime') {
      return (
        <Input
          type="datetime-local"
          value={item.dateTimeValue ?? ''}
          onChange={(e) => updateItemField(item, 'dateTimeValue', e.target.value)}
          className="h-8 text-[11px]"
          disabled={isReadOnly}
        />
      );
    }

    if (criteria === 'Table') {
      const table = normalizeTableData(item);
      return (
        <div className="grid grid-cols-2 gap-1">
          <Input
            type="number"
            min={1}
            value={table.rows.length}
            onChange={(e) => resizeTable(item, Number(e.target.value), table.columns.length)}
            className="h-8 text-[11px]"
            placeholder="Rows"
            disabled={isReadOnly}
          />
          <Input
            type="number"
            min={1}
            value={table.columns.length}
            onChange={(e) => resizeTable(item, table.rows.length, Number(e.target.value))}
            className="h-8 text-[11px]"
            placeholder="Columns"
            disabled={isReadOnly}
          />
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 gap-1">
        {showMin ? (
          <Input
            type="number"
            value={item.minValue ?? ''}
            onChange={(e) => updateItemField(item, 'minValue', e.target.value)}
            className="h-8 text-[11px]"
            placeholder="Min"
            disabled={isReadOnly}
          />
        ) : null}
        {showMax ? (
          <Input
            type="number"
            value={item.comparator === 'BETWEEN' ? item.maxValue ?? '' : item.value ?? ''}
            onChange={(e) => updateItemField(item, item.comparator === 'BETWEEN' ? 'maxValue' : 'value', e.target.value)}
            className="h-8 text-[11px]"
            placeholder={item.comparator === '=' ? 'Value' : 'Max'}
            disabled={isReadOnly}
          />
        ) : null}
      </div>
    );
  };

  const needsAdvancedColumns = section.items.some((item) => {
    const criteria = item.criteria || 'Accept/Reject/NA';
    return criteria !== 'Accept/Reject/NA' && criteria !== 'Text';
  });

  const renderTableEditor = (item: ChecklistItem) => {
    if ((item.criteria || 'Accept/Reject/NA') !== 'Table') return null;
    const table = normalizeTableData(item);
    return (
      <tr className="border-b border-slate-100 bg-slate-50/60">
        <td colSpan={needsAdvancedColumns ? 7 : 4} className="p-2">
          <div className="overflow-visible rounded border border-slate-200 bg-white">
            <table className="w-full min-w-[760px] table-fixed border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50">
                  <th className="w-[150px] border border-slate-200 p-2 text-left">Rows / Columns</th>
                  {table.columns.map((column, columnIndex) => (
                    <th key={columnIndex} className="border border-slate-200 p-1">
                      <Input
                        value={column}
                        onChange={(e) =>
                          updateTable(item, (t) => ({
                            ...t,
                            columns: t.columns.map((c, i) => (i === columnIndex ? e.target.value : c)),
                          }))
                        }
                        className="h-8 text-center text-[11px]"
                        disabled={isReadOnly}
                      />
                    </th>
                  ))}
                  <th className="w-[110px] border border-slate-200 p-2">Unit</th>
                  <th className="w-[120px] border border-slate-200 p-2">Comparator</th>
                  <th className="w-[100px] border border-slate-200 p-2">Min</th>
                  <th className="w-[100px] border border-slate-200 p-2">Value</th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td className="border border-slate-200 p-1">
                      <Input
                        value={row.name}
                        onChange={(e) =>
                          updateTable(item, (t) => ({
                            ...t,
                            rows: t.rows.map((r, i) => (i === rowIndex ? { ...r, name: e.target.value } : r)),
                          }))
                        }
                        className="h-8 text-center text-[11px]"
                        disabled={isReadOnly}
                      />
                    </td>
                    {table.columns.map((_, columnIndex) => (
                      <td key={columnIndex} className="border border-slate-200 p-1">
                        <Input
                          value={row.values[columnIndex] || ''}
                          onChange={(e) =>
                            updateTable(item, (t) => ({
                              ...t,
                              rows: t.rows.map((r, i) =>
                                i === rowIndex
                                  ? { ...r, values: r.values.map((v, j) => (j === columnIndex ? e.target.value : v)) }
                                  : r,
                              ),
                            }))
                          }
                          className="h-8 text-[11px]"
                          disabled={isReadOnly}
                        />
                      </td>
                    ))}
                    <td className="border border-slate-200 p-1">
                      <Select
                        value={row.unit || 'N/A'}
                        onValueChange={(v) =>
                          updateTable(item, (t) => ({
                            ...t,
                            rows: t.rows.map((r, i) => (i === rowIndex ? { ...r, unit: v } : r)),
                          }))
                        }
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className="h-8 bg-white text-[11px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNIT_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option} className="text-xs">
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-slate-200 p-1">
                      <Select
                        value={row.comparator || '='}
                        onValueChange={(v) =>
                          updateTable(item, (t) => ({
                            ...t,
                            rows: t.rows.map((r, i) => (i === rowIndex ? { ...r, comparator: v } : r)),
                          }))
                        }
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className="h-8 bg-white text-[11px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COMPARATOR_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option} className="text-xs">
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border border-slate-200 p-1">
                      <Input
                        type="number"
                        value={row.minValue || ''}
                        onChange={(e) =>
                          updateTable(item, (t) => ({
                            ...t,
                            rows: t.rows.map((r, i) => (i === rowIndex ? { ...r, minValue: e.target.value } : r)),
                          }))
                        }
                        className="h-8 text-[11px]"
                        disabled={isReadOnly}
                      />
                    </td>
                    <td className="border border-slate-200 p-1">
                      <Input
                        type="number"
                        value={row.maxValue || ''}
                        onChange={(e) =>
                          updateTable(item, (t) => ({
                            ...t,
                            rows: t.rows.map((r, i) => (i === rowIndex ? { ...r, maxValue: e.target.value } : r)),
                          }))
                        }
                        className="h-8 text-[11px]"
                        disabled={isReadOnly}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="rounded border border-slate-200 bg-white">
      {showHeader ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-2 py-2">
          {!hideCategoryHeader ? (
            <>
              <Label className="text-[11px] font-medium text-slate-700 shrink-0">Category</Label>
              <Select value={section.category} onValueChange={(v) => onUpdateSectionCategory(section.id, v)}>
                <SelectTrigger className="h-8 w-[min(280px,42vw)] text-xs bg-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {COATING_CATEGORY_OPTIONS.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-xs">
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          ) : null}
          {canRemoveSection ? (
            <Button type="button" variant="ghost" size="sm" className="h-8 text-xs text-destructive" onClick={() => onRemoveSection(section.id)}>
              Remove block
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-3 p-3">
        {section.items.length > 0 ? (
          <div className="overflow-visible">
            <table className={`w-full table-fixed border-collapse text-xs ${needsAdvancedColumns ? 'min-w-[980px]' : 'min-w-[620px]'}`}>
              <colgroup>
                {needsAdvancedColumns ? (
                  <>
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '4%' }} />
                  </>
                ) : (
                  <>
                    <col style={{ width: '52%' }} />
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '6%' }} />
                  </>
                )}
              </colgroup>
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-2 py-2 text-left font-medium text-slate-800">Description</th>
                  <th className="px-2 py-2 text-left font-medium text-slate-800">Criteria</th>
                  {needsAdvancedColumns ? (
                    <>
                      <th className="px-2 py-2 text-left font-medium text-slate-800">Unit</th>
                      <th className="px-2 py-2 text-left font-medium text-slate-800">Comparator</th>
                      <th className="px-2 py-2 text-left font-medium text-slate-800">Entry</th>
                    </>
                  ) : null}
                  <th className="px-2 py-2 text-left font-medium text-slate-800">Options</th>
                  <th className="px-0 py-2 text-center">
                    <span className="sr-only">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {section.items.map((item) => {
                  const criteria = item.criteria || 'Accept/Reject/NA';
                  const showUnitComparator = criteria !== 'Accept/Reject/NA' && criteria !== 'Text';
                  return (
                    <React.Fragment key={item.id}>
                      <tr className="border-b border-slate-100 hover:bg-slate-50/60">
                        <td className="min-w-0 p-1.5 align-top">
                          <Input
                            value={item.description}
                            onChange={(e) => onUpdateItemDescription(section.id, item.id, e.target.value)}
                            className="h-8 w-full min-w-0 rounded border-slate-200 text-[11px] bg-white"
                            placeholder="Description"
                          />
                        </td>
                        <td className="min-w-0 p-1.5 align-top">
                          <Select value={criteria} onValueChange={(v) => updateItemField(item, 'criteria', v)} disabled={isReadOnly}>
                            <SelectTrigger className="h-8 w-full bg-white text-[11px]">
                              <SelectValue placeholder="Criteria" />
                            </SelectTrigger>
                            <SelectContent>
                              {CRITERIA_OPTIONS.map((option) => (
                                <SelectItem key={option} value={option} className="text-xs">
                                  {CRITERIA_LABELS[option]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        {needsAdvancedColumns ? (
                          <>
                          <td className="min-w-0 p-1.5 align-top">
                          {showUnitComparator ? (
                            <Select
                              value={item.unit || 'N/A'}
                              onValueChange={(v) => updateItemField(item, 'unit', v)}
                              disabled={isReadOnly || criteria === 'Dateandtime'}
                            >
                              <SelectTrigger className="h-8 w-full bg-white text-[11px]">
                                <SelectValue placeholder="Unit" />
                              </SelectTrigger>
                              <SelectContent>
                                {UNIT_OPTIONS.map((option) => (
                                  <SelectItem key={option} value={option} className="text-xs">
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : null}
                          </td>
                          <td className="min-w-0 p-1.5 align-top">
                          {showUnitComparator ? (
                            <Select
                              value={item.comparator || (criteria === 'Value' || criteria === 'Table' ? '=' : 'None')}
                              onValueChange={(v) => updateItemField(item, 'comparator', v)}
                              disabled={isReadOnly || criteria === 'Dateandtime'}
                            >
                              <SelectTrigger className="h-8 w-full bg-white text-[11px]">
                                <SelectValue placeholder="Comparator" />
                              </SelectTrigger>
                              <SelectContent>
                                {COMPARATOR_OPTIONS.map((option) => (
                                  <SelectItem key={option} value={option} className="text-xs">
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : null}
                          </td>
                          <td className="min-w-0 p-1.5 align-top">{renderEntry(item)}</td>
                          </>
                        ) : null}
                        <td className="p-1.5 align-top">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:text-cyan-700 disabled:opacity-50"
                              onClick={() => updateItemField(item, 'checkboxRequired', !item.checkboxRequired)}
                              disabled={isReadOnly}
                              title="Checkbox required"
                            >
                              {item.checkboxRequired ? <Check className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                            </button>
                            <button
                              type="button"
                              className={`inline-flex h-8 w-8 items-center justify-center rounded border bg-white hover:text-cyan-700 disabled:opacity-50 ${
                                item.fileUploadRequired ? 'border-cyan-400 text-cyan-700' : 'border-slate-200 text-slate-500'
                              }`}
                              onClick={() => updateItemField(item, 'fileUploadRequired', !item.fileUploadRequired)}
                              disabled={isReadOnly}
                              title="File upload required"
                            >
                              <FileUp className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="p-0 align-middle text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-500 hover:text-destructive"
                            disabled={section.items.length <= 1}
                            onClick={() => onRemoveItem(section.id, item.id)}
                            aria-label="Remove item row"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                      {renderTableEditor(item)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded border border-dashed border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
            No checklist rows in this category.
          </div>
        )}

        {onAddItem ? (
          <Button
            type="button"
            size="sm"
            className="h-8 gap-1 bg-hero-red text-xs text-white hover:bg-hero-red-60"
            onClick={() => onAddItem(section.id)}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add item
          </Button>
        ) : null}
      </div>
      {sectionFooter ? sectionFooter(section) : null}
    </div>
  );
}

export function CoatingChecklistTemplateTable({
  sections,
  onUpdateSectionCategory,
  onRemoveSection,
  onRemoveItem,
  onUpdateItemDescription,
  onUpdateItemField,
  onAddItem,
  sectionFooter,
  hideCategoryHeader,
}: CoatingChecklistTemplateTableProps) {
  if (sections.length === 0) return null;

  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <SectionBlock
          key={section.id}
          section={section}
          canRemoveSection={sections.length > 1}
          onUpdateSectionCategory={onUpdateSectionCategory}
          onRemoveSection={onRemoveSection}
          onRemoveItem={onRemoveItem}
          onUpdateItemDescription={onUpdateItemDescription}
          onUpdateItemField={onUpdateItemField}
          onAddItem={onAddItem}
          sectionFooter={sectionFooter}
          hideCategoryHeader={hideCategoryHeader}
        />
      ))}
    </div>
  );
}
